import { useState, useEffect } from "react";
import { QualityInspection, InspectedItem } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Package,
  FileText,
  Calendar,
  User,
  Plus,
  Trash2,
  Save
} from "lucide-react";
import { toast } from "sonner";

interface QualityInspectionFormProps {
  inspection?: QualityInspection;
  onSave: (inspectionData: Partial<QualityInspection>) => Promise<boolean>;
  onCancel: () => void;
}

export const QualityInspectionForm = ({ 
  inspection, 
  onSave, 
  onCancel 
}: QualityInspectionFormProps) => {
  const { state } = useProcurement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  // Form data
  const [formData, setFormData] = useState({
    inspectionId: "",
    poNumber: "",
    inspectorName: "",
    inspectionDate: new Date().toISOString().split('T')[0],
    overallStatus: "Pending" as "Pending" | "In Progress" | "Passed" | "Failed",
    notes: "",
    inspectedItems: [] as InspectedItem[]
  });

  // Initialize form data
  useEffect(() => {
    if (inspection) {
      setFormData({
        inspectionId: inspection.inspectionId,
        poNumber: inspection.poNumber,
        inspectorName: inspection.inspectorName,
        inspectionDate: inspection.inspectionDate.split('T')[0],
        overallStatus: inspection.overallStatus,
        notes: inspection.notes || "",
        inspectedItems: inspection.inspectedItems || []
      });
    } else {
      // Generate new inspection ID
      const newId = `QI-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setFormData(prev => ({
        ...prev,
        inspectionId: newId
      }));
    }
  }, [inspection]);

  // Get available POs
  const availablePOs = state.purchaseOrders.filter(po => 
    po.status === "Delivered" || po.status === "In Transit"
  );

  // Get SKUs for selected PO
  const selectedPOKUs = formData.poNumber 
    ? state.purchaseOrders.find(po => po.poNumber === formData.poNumber)?.items || []
    : [];

  // Add new inspected item
  const addInspectedItem = (skuId: number) => {
    const sku = state.skus.find(s => s.id === skuId);
    const poItem = selectedPOKUs.find(item => item.skuId === skuId);
    
    if (sku && poItem) {
      const newItem: InspectedItem = {
        id: Date.now(),
        skuId: skuId,
        skuCode: sku.skuCode,
        skuName: sku.name,
        quantityOrdered: poItem.quantity,
        quantityInspected: 0,
        quantityPassed: 0,
        quantityFailed: 0,
        status: "Pending",
        defects: [],
        notes: ""
      };
      
      setFormData(prev => ({
        ...prev,
        inspectedItems: [...prev.inspectedItems, newItem]
      }));
    }
  };

  // Update inspected item
  const updateInspectedItem = (itemId: number, field: keyof InspectedItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      inspectedItems: prev.inspectedItems.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  // Remove inspected item
  const removeInspectedItem = (itemId: number) => {
    setFormData(prev => ({
      ...prev,
      inspectedItems: prev.inspectedItems.filter(item => item.id !== itemId)
    }));
  };

  // Add defect to item
  const addDefect = (itemId: number) => {
    const newDefect = {
      id: Date.now(),
      type: "",
      description: "",
      severity: "Minor" as "Minor" | "Major" | "Critical"
    };
    
    updateInspectedItem(itemId, "defects", [
      ...formData.inspectedItems.find(item => item.id === itemId)?.defects || [],
      newDefect
    ]);
  };

  // Update defect
  const updateDefect = (itemId: number, defectId: number, field: string, value: any) => {
    const item = formData.inspectedItems.find(i => i.id === itemId);
    if (item) {
      const updatedDefects = item.defects.map(defect =>
        defect.id === defectId ? { ...defect, [field]: value } : defect
      );
      updateInspectedItem(itemId, "defects", updatedDefects);
    }
  };

  // Remove defect
  const removeDefect = (itemId: number, defectId: number) => {
    const item = formData.inspectedItems.find(i => i.id === itemId);
    if (item) {
      const updatedDefects = item.defects.filter(defect => defect.id !== defectId);
      updateInspectedItem(itemId, "defects", updatedDefects);
    }
  };

  // Calculate overall status based on items
  const calculateOverallStatus = () => {
    if (formData.inspectedItems.length === 0) return "Pending";
    
    const allPassed = formData.inspectedItems.every(item => item.status === "Passed");
    const anyFailed = formData.inspectedItems.some(item => item.status === "Failed");
    const anyInProgress = formData.inspectedItems.some(item => item.status === "In Progress");
    
    if (allPassed) return "Passed";
    if (anyFailed) return "Failed";
    if (anyInProgress) return "In Progress";
    return "Pending";
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.poNumber || !formData.inspectorName) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.inspectedItems.length === 0) {
      toast.error("Please add at least one item to inspect");
      return;
    }

    setIsSubmitting(true);

    const inspectionData = {
      ...formData,
      inspectionDate: new Date(formData.inspectionDate).toISOString(),
      overallStatus: calculateOverallStatus(),
      createdAt: inspection?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const success = await onSave(inspectionData);
    setIsSubmitting(false);

    if (success) {
      toast.success("Quality inspection saved successfully");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Passed": return "text-green-600";
      case "Failed": return "text-red-600";
      case "In Progress": return "text-blue-600";
      default: return "text-yellow-600";
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Critical": return "bg-red-100 text-red-800";
      case "Major": return "bg-orange-100 text-orange-800";
      case "Minor": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {inspection ? "Edit Quality Inspection" : "Create Quality Inspection"}
          </DialogTitle>
          <DialogDescription>
            {inspection ? "Update the quality inspection details" : "Create a new quality inspection for delivered items"}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="items">Items Inspection</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Inspection Details</CardTitle>
                <CardDescription>Basic information about the quality inspection</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inspectionId">Inspection ID *</Label>
                    <Input
                      id="inspectionId"
                      value={formData.inspectionId}
                      onChange={(e) => setFormData(prev => ({ ...prev, inspectionId: e.target.value }))}
                      disabled
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="poNumber">Purchase Order *</Label>
                    <Select 
                      value={formData.poNumber} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, poNumber: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select PO" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePOs.map(po => (
                          <SelectItem key={po.id} value={po.poNumber}>
                            {po.poNumber} - {state.vendors.find(v => v.id === po.vendorId)?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inspectorName">Inspector Name *</Label>
                    <Input
                      id="inspectorName"
                      value={formData.inspectorName}
                      onChange={(e) => setFormData(prev => ({ ...prev, inspectorName: e.target.value }))}
                      placeholder="Enter inspector name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inspectionDate">Inspection Date *</Label>
                    <Input
                      id="inspectionDate"
                      type="date"
                      value={formData.inspectionDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, inspectionDate: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="overallStatus">Overall Status</Label>
                    <Select 
                      value={formData.overallStatus} 
                      onValueChange={(value: any) => setFormData(prev => ({ ...prev, overallStatus: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Passed">Passed</SelectItem>
                        <SelectItem value="Failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about the inspection..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Items Inspection Tab */}
          <TabsContent value="items" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Items to Inspect</CardTitle>
                <CardDescription>
                  Add items from the selected PO for quality inspection
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedPOKUs.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedPOKUs
                        .filter(poItem => !formData.inspectedItems.some(item => item.skuId === poItem.skuId))
                        .map(poItem => {
                          const sku = state.skus.find(s => s.id === poItem.skuId);
                          return (
                            <Card key={poItem.skuId} className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium">{sku?.name}</h4>
                                  <p className="text-sm text-muted-foreground">{sku?.skuCode}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Ordered: {poItem.quantity} {sku?.unit}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => addInspectedItem(poItem.skuId)}
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add
                                </Button>
                              </div>
                            </Card>
                          );
                        })}
                    </div>

                    {formData.inspectedItems.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="font-semibold">Items Being Inspected</h4>
                        {formData.inspectedItems.map((item) => (
                          <Card key={item.id} className="p-4">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium">{item.skuName}</h5>
                                  <p className="text-sm text-muted-foreground">{item.skuCode}</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeInspectedItem(item.id)}
                                  className="text-red-500"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="space-y-1">
                                  <Label className="text-xs">Quantity Inspected</Label>
                                  <Input
                                    type="number"
                                    value={item.quantityInspected}
                                    onChange={(e) => updateInspectedItem(item.id, 'quantityInspected', parseInt(e.target.value) || 0)}
                                    min="0"
                                    max={item.quantityOrdered}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Quantity Passed</Label>
                                  <Input
                                    type="number"
                                    value={item.quantityPassed}
                                    onChange={(e) => updateInspectedItem(item.id, 'quantityPassed', parseInt(e.target.value) || 0)}
                                    min="0"
                                    max={item.quantityInspected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Quantity Failed</Label>
                                  <Input
                                    type="number"
                                    value={item.quantityFailed}
                                    onChange={(e) => updateInspectedItem(item.id, 'quantityFailed', parseInt(e.target.value) || 0)}
                                    min="0"
                                    max={item.quantityInspected}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Status</Label>
                                  <Select 
                                    value={item.status} 
                                    onValueChange={(value: any) => updateInspectedItem(item.id, 'status', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Pending">Pending</SelectItem>
                                      <SelectItem value="In Progress">In Progress</SelectItem>
                                      <SelectItem value="Passed">Passed</SelectItem>
                                      <SelectItem value="Failed">Failed</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs">Defects</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addDefect(item.id)}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Defect
                                  </Button>
                                </div>
                                
                                {item.defects.map((defect) => (
                                  <Card key={defect.id} className="p-3">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                      <Input
                                        placeholder="Defect type"
                                        value={defect.type}
                                        onChange={(e) => updateDefect(item.id, defect.id, 'type', e.target.value)}
                                      />
                                      <Input
                                        placeholder="Description"
                                        value={defect.description}
                                        onChange={(e) => updateDefect(item.id, defect.id, 'description', e.target.value)}
                                      />
                                      <div className="flex gap-2">
                                        <Select 
                                          value={defect.severity} 
                                          onValueChange={(value: any) => updateDefect(item.id, defect.id, 'severity', value)}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Minor">Minor</SelectItem>
                                            <SelectItem value="Major">Major</SelectItem>
                                            <SelectItem value="Critical">Critical</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => removeDefect(item.id, defect.id)}
                                          className="text-red-500"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </Card>
                                ))}
                              </div>

                              <div className="space-y-1">
                                <Label className="text-xs">Item Notes</Label>
                                <Textarea
                                  value={item.notes}
                                  onChange={(e) => updateInspectedItem(item.id, 'notes', e.target.value)}
                                  placeholder="Notes about this item..."
                                  rows={2}
                                />
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Please select a Purchase Order first</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Inspection Summary</CardTitle>
                <CardDescription>Review the inspection details before saving</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Inspection Details</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">ID:</span> {formData.inspectionId}</p>
                      <p><span className="text-muted-foreground">PO:</span> {formData.poNumber}</p>
                      <p><span className="text-muted-foreground">Inspector:</span> {formData.inspectorName}</p>
                      <p><span className="text-muted-foreground">Date:</span> {new Date(formData.inspectionDate).toLocaleDateString()}</p>
                      <p><span className="text-muted-foreground">Status:</span> 
                        <Badge variant="outline" className="ml-2">
                          {formData.overallStatus}
                        </Badge>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Items Summary</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Total Items:</span> {formData.inspectedItems.length}</p>
                      <p><span className="text-muted-foreground">Passed:</span> 
                        <span className="text-green-600 ml-1">
                          {formData.inspectedItems.filter(item => item.status === "Passed").length}
                        </span>
                      </p>
                      <p><span className="text-muted-foreground">Failed:</span> 
                        <span className="text-red-600 ml-1">
                          {formData.inspectedItems.filter(item => item.status === "Failed").length}
                        </span>
                      </p>
                      <p><span className="text-muted-foreground">In Progress:</span> 
                        <span className="text-blue-600 ml-1">
                          {formData.inspectedItems.filter(item => item.status === "In Progress").length}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                {formData.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm bg-muted/50 p-3 rounded">{formData.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Inspection
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
